import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * AdminPointsRedemptionTiersPage — full "Points Redemption Tiers" table view.
 * No sidebar or header here; this is content-only, dropped into whatever shell
 * layout the app already provides.
 * ---------------------------------------------------------------------- */

const getDeleteErrorMessage = (error) => {
  const responseData = error.response?.data;
  const extractedMessage = extractErrorMessage(error);

  return (typeof responseData?.message === "string" && responseData.message.trim())
    || (typeof responseData?.error === "string" && responseData.error.trim())
    || (typeof extractedMessage === "string" && extractedMessage.trim() && extractedMessage !== "[object Object]" && extractedMessage)
    || "Unable to delete the redemption tier.";
};

const AdminPointsRedemptionTiersPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Points Redemption Tiers | Omaha Food Lovers";
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tierToDelete, setTierToDelete] = useState(null);
  const [deletingTierId, setDeletingTierId] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "name", label: "Name", width: "1.5fr" },
    { key: "amount", label: "Amount ($)", width: "1fr", render: (r) => `$${(r.amount_cents / 100).toFixed(2)}` },
    { key: "points_required", label: "Points Required", width: "1fr" },
    { key: "sort_order", label: "Sort Order", width: "0.8fr" },
    { key: "isActive", label: "Status", width: "0.8fr", render: (r) => (r.is_active ? "Active" : "Inactive") },
    { key: "createdAt", label: "Created At", width: "1.2fr", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "N/A" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/admin/points-redemption-tiers/${row.id}/edit`, { state: { tierData: row } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformTierData = (tiersArray) =>
    tiersArray.map((tier) => ({
      id: tier.id,
      name: tier.name || "N/A",
      amount_cents: tier.amount_cents || 0,
      points_required: tier.points_required || 0,
      is_active: tier.is_active !== false,
      sort_order: tier.sort_order || 0,
      created_at: tier.created_at,
    }));

  const fetchTiers = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminPointsRedemptionTiers}?${buildQueryParams(pageNum)}`);

      if (response.data?.status || response.data?.success) {
        const data = response.data.data;
        const newTiers = data.data || [];
        const transformedTiers = transformTierData(newTiers);

        if (isLoadMore) {
          setTiers((prev) => [...prev, ...transformedTiers]);
        } else {
          setTiers(transformedTiers);
        }

        setHasMore(data.current_page < data.last_page);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch points redemption tiers:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchTiers(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (tierId) => {
    setTierToDelete(tierId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!tierToDelete || deletingTierId) return;

    try {
      setDeletingTierId(tierToDelete);
      const response = await del(endpoints.adminPointsRedemptionTier.replace('{{id}}', tierToDelete));
      if (response.data?.success || response.data?.status) {
        toastr.success("Points redemption tier deleted successfully");
        setShowDeleteModal(false);
        setTierToDelete(null);
        fetchTiers(1, false);
      }
    } catch (error) {
      console.error("Failed to delete points redemption tier:", error);
      const responseData = error.response?.data;
      const errorMessage = getDeleteErrorMessage(error);

      if (responseData?.error_code === "redemption_tier_has_active_vouchers") {
        toastr.error(errorMessage, "Cannot Delete Redemption Tier");
      } else {
        toastr.error(errorMessage);
      }
    } finally {
      setDeletingTierId(null);
    }
  };

  const cancelDelete = () => {
    if (deletingTierId) return;
    setShowDeleteModal(false);
    setTierToDelete(null);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "sort_order",
      options: [
        { value: "name", label: "Name" },
        { value: "amount_cents", label: "Amount" },
        { value: "points_required", label: "Points Required" },
        { value: "is_active", label: "Status" },
        { value: "sort_order", label: "Sort Order" },
        { value: "created_at", label: "Created At" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "asc",
    },
  ];

  useEffect(() => {
    fetchTiers(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Points Redemption Tiers</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of points redemption tiers and thresholds.</p>
            </div>
            <SecondaryButton
              variant="orange"
              icon={tickIcon}
              onClick={() => navigate("/admin/points-redemption-tiers/create")}
            >
              Add Tier
            </SecondaryButton>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name"
            filters={filters}
          />

          <DataTable
            columns={columns}
            rows={tiers}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Points Redemption Tier"
        message="Are you sure you want to delete this points redemption tier? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="points redemption tier"
        confirmButtonText={deletingTierId === tierToDelete ? "Deleting..." : "Delete"}
        confirmButtonDisabled={deletingTierId === tierToDelete}
      />
    </div>
  );
};

AdminPointsRedemptionTiersPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminPointsRedemptionTiersPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminPointsRedemptionTiersPage;
